import { useState, type FormEvent } from 'react';
import { ErrorBanner, TextField, useT, type ErrorEnvelopeView } from '@skm/ui';
import { ApiError } from '../api/client.ts';
import { EnrolmentDetails, SavedCodesCheck } from './Enrolment.tsx';
import { AuthLayout } from './AuthLayout.tsx';
import { errorView } from './errors.ts';
import { MFA_EXPIRED_KEY, type Auth, type Enrolment, type Principal } from './session.ts';

export interface SignInProps {
  readonly auth: Auth;
  readonly onSignedIn: (p: Principal) => void;
}

/** Shown when the API refuses the re-enrolment (404/403): an administrator has to finish the set-up. */
const ENROL_UNAVAILABLE: ErrorEnvelopeView = {
  code: 'MFA_REQUIRED',
  messageKey: 'frame.enrol.unavailable',
};

/**
 * Sign-in: email + password, then the TOTP code [MB D3]. The API provisions every user with a second factor; after
 * an administrator reset it, the password step leads to re-enrolment (new key + recovery codes, then a code) before
 * the session counts [CR-W2a-38, CR-W2a-42 Q4].
 */
export function SignIn({ auth, onSignedIn }: SignInProps) {
  const t = useT();
  const [step, setStep] = useState<'password' | 'totp' | 'enrol'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<ErrorEnvelopeView | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.body.messageKey === MFA_EXPIRED_KEY) setStep('password');
      setCode('');
      setError(errorView(e));
    } finally {
      setBusy(false);
    }
  };
  const enrol = async () => {
    try {
      setEnrolment(await auth.startEnrol(password));
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
        setError(ENROL_UNAVAILABLE);
        return;
      }
      throw e;
    }
    setPassword('');
    setStep('enrol');
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (step === 'password') {
        const r = await auth.signIn(email, password);
        if (r.status === 'ok') onSignedIn(r.principal);
        else if (r.status === 'enrol') await enrol();
        else setStep('totp');
      } else onSignedIn(await auth.verifyTotp(code));
    });
  };
  const codeField = (
    <TextField
      label={t('frame.mfa.code')}
      inputMode="numeric"
      autoComplete="one-time-code"
      value={code}
      onChange={(e) => setCode(e.target.value)}
    />
  );
  if (step === 'enrol' && enrolment)
    return (
      <AuthLayout>
        <main className="skm-signin">
          <h1>{t('frame.enrol.title')}</h1>
          <p>{t('frame.enrol.resetIntro')}</p>
          <ErrorBanner error={error} />
          <EnrolmentDetails enrolment={enrolment} />
          <form onSubmit={submit}>
            <SavedCodesCheck checked={saved} onChange={setSaved} />
            <p>{t('frame.enrol.codeHint')}</p>
            {codeField}
            <button type="submit" disabled={busy || !saved}>
              {t('frame.enrol.verify')}
            </button>
          </form>
        </main>
      </AuthLayout>
    );
  return (
    <AuthLayout>
      <main className="skm-signin">
        <h1>{t('frame.signIn.title')}</h1>
        <ErrorBanner error={error} />
        <form onSubmit={submit}>
          {step === 'password' ? (
            <>
              <TextField
                label={t('frame.signIn.email')}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label={t('frame.signIn.password')}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          ) : (
            <>
              <p>{t('frame.mfa.hint')}</p>
              {codeField}
            </>
          )}
          <button type="submit" disabled={busy}>
            {t(step === 'password' ? 'frame.signIn.submit' : 'frame.mfa.verify')}
          </button>
        </form>
      </main>
    </AuthLayout>
  );
}
